"use client";

import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import { dateFormats } from "@/utils/dateFormatter";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import SectionHeader from "./SectionHeader";
import TabSwitcher from "./TabSwitcher";
import FlightTicketCard from "./pages/flight/FlightTicketCard";

// Flight Ticket Card Component

export default function TicketShowCase({
  title,
  description,
  linkText,
  href,
  preserveTrailingSlash = false,
  items = [],
  componentType,
  componentProps = {},
  showTabs = false,
  tabs: customTabs = [],
  onTabChange,
}) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslationDetector();

  // Default tabs by component type
  const defaultTabsByComponentType = useMemo(() => {
    return {
      FlightTicketsTab: [
        { id: 1, label: t("flight.oneWay") },
        { id: 2, label: t("flight.roundTrip") },
      ],
    };
  }, [locale]);

  // Tabs'ı hesapla - useMemo ile stabilize et
  const calculatedTabs = useMemo(() => {
    if (customTabs && customTabs.length > 0) {
      return customTabs;
    } else if (
      showTabs &&
      componentType &&
      defaultTabsByComponentType?.[componentType]
    ) {
      return defaultTabsByComponentType?.[componentType] || [];
    }
    return [];
  }, [componentType, showTabs, customTabs, defaultTabsByComponentType]);

  const tabItemIds = useMemo(() => {
    const tabItems = componentProps?.tabItems;
    if (!tabItems || typeof tabItems !== "object") return [];
    return Object.keys(tabItems)
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n));
  }, [componentProps?.tabItems]);

  const effectiveTabs = useMemo(() => {
    // If tabItems exists, only show tabs that have data keys.
    if (showTabs && tabItemIds.length > 0) {
      const set = new Set(tabItemIds);
      return (calculatedTabs || []).filter((t) => set.has(Number(t.id)));
    }
    return calculatedTabs || [];
  }, [showTabs, tabItemIds, calculatedTabs]);

  const [activeTab, setActiveTab] = useState(() => {
    return calculatedTabs.length > 0 ? calculatedTabs[0].id : 1;
  });

  useEffect(() => {
    if (!showTabs) return;
    if (effectiveTabs.length === 0) return;
    const isActiveTabValid = effectiveTabs.some((t) => t.id === activeTab);
    if (!isActiveTabValid) {
      setActiveTab(effectiveTabs[0].id);
    }
  }, [showTabs, effectiveTabs, activeTab]);
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  // Flight card'a tıklandığında flight-listing sayfasına yönlendirme
  const handleFlightCardClick = useCallback(
    (flight) => {
      if (!flight || !flight.Origin || !flight.Destination) {
        return;
      }

      const origin = flight.Origin;
      const destination = flight.Destination;
      const isRoundTrip =
        flight.IncomingDepartureDateTime || flight.IncomingDepartureDate;

      // Tarihleri parse et (YYYY-MM-DD formatına)
      const departureDate = flight.OutgoingDepartureDateTime
        ? dateFormats.iso(flight.OutgoingDepartureDateTime)
        : null;

      const returnDate = isRoundTrip
        ? flight.IncomingDepartureDateTime
          ? dateFormats.iso(flight.IncomingDepartureDateTime)
          : null
        : null;

      // Tarih bilgisi yoksa işlemi durdur
      if (!departureDate) {
        return;
      }

      // Round-trip için return date kontrolü
      if (isRoundTrip && !returnDate) {
        return;
      }

      // Airport info oluştur: sadece şehir slug'ları (BarChartCalendarGraph mantığına uygun)
      // Havalimanı kodları çıkarıldı - sadece şehir isimleri kullanılıyor
      const originSlug = origin.Slug || origin.CitySlugName;
      const destSlug = destination.Slug || destination.CitySlugName;

      if (!originSlug || !destSlug) {
        return;
      }

      // Sadece şehir slug'larını kullan (havalimanı kodları yok)
      const airportInfo = `${originSlug}-${destSlug}`;

      // Locale'e göre URL path'ini belirle (BarChartCalendarGraph mantığına uygun)
      // Türkçe: /ucak-bileti/tek-yon, diğer diller: /flight/one-way
      const oneWayPath =
        locale === "tr" ? "ucak-bileti/tek-yon" : "flight/one-way";
      const roundTripPath =
        locale === "tr" ? "ucak-bileti/gidis-donus" : "flight/round-trip";

      // URL oluştur
      const baseUrl = isRoundTrip
        ? `/${roundTripPath}/${airportInfo}/${departureDate}/${returnDate}`
        : `/${oneWayPath}/${airportInfo}/${departureDate}`;

      // Query parametreleri (varsayılan adult=1)
      const queryParams = new URLSearchParams();
      queryParams.set("adult", "1");

      const finalUrl = queryParams.toString()
        ? `${baseUrl}?${queryParams.toString()}`
        : baseUrl;

      // localStorage'a kaydet (FlightSearchForm ile aynı format)
      try {
        // departureCity ve returnCity formatını oluştur
        const departureCityData = {
          cityName: origin.LocalizedCityName,
          value: origin.Name,
          isCity: origin.IsCity || false,
          code: origin.Code || origin.CityCode,
          id: origin.Slug || origin.CitySlugName,
        };

        const returnCityData = {
          cityName: destination.LocalizedCityName,
          value: destination.Name,
          isCity: destination.IsCity || false,
          code: destination.Code || destination.CityCode,
          id: destination.Slug || destination.CitySlugName,
        };

        // Tarihleri Date objesine çevir
        const departureDateRaw = flight.OutgoingDepartureDateTime
          ? new Date(flight.OutgoingDepartureDateTime)
          : departureDate
            ? new Date(departureDate)
            : null;
        const returnDateRaw = flight.IncomingDepartureDateTime
          ? new Date(flight.IncomingDepartureDateTime)
          : returnDate
            ? new Date(returnDate)
            : null;

        // localStorage'a kaydedilecek format
        const newSearch = {
          departureCity: departureCityData,
          returnCity: returnCityData,
          isOneWay: !isRoundTrip,
          departureDateRaw: departureDateRaw,
          departureDate: departureDateRaw
            ? dateFormats.dayShortMonth(departureDateRaw, locale)
            : null,
          returnDateRaw: returnDateRaw,
          returnDate: returnDateRaw
            ? dateFormats.dayShortMonth(returnDateRaw, locale)
            : null,
        };

        // Mevcut arama geçmişini al
        const existingSearches = JSON.parse(
          localStorage.getItem("latestFlightSearches") || "[]",
        );

        // Yeni aramayı array'in başına ekle
        const updatedSearches = [newSearch, ...existingSearches];

        // localStorage'a kaydet
        localStorage.setItem(
          "latestFlightSearches",
          JSON.stringify(updatedSearches),
        );
      } catch (error) {
        // localStorage kaydı başarısız olsa bile yönlendirme devam etsin
        console.error("localStorage kaydı başarısız:", error);
      }

      // Yönlendir
      router.push(finalUrl);
    },
    [router, locale],
  );

  // Tab'lı section için items'ı belirle
  const getCurrentItems = () => {
    // Debug için veri yapısını logla

    // Eğer tabItems prop'u varsa ve showTabs aktifse, o tab'ın items'ını kullan
    if (
      showTabs &&
      componentProps.tabItems &&
      componentProps.tabItems[activeTab]
    ) {
      const currentItems = componentProps.tabItems[activeTab];
      return Array.isArray(currentItems) ? currentItems : [];
    }

    // Component type'a göre filtreleme yap
    if (showTabs && items) {
      const filteredItems = filterItemsByTab(items, activeTab, componentType);
      return filteredItems;
    }

    const fallbackItems = Array.isArray(items) ? items : [];
    return fallbackItems;
  };

  // Items'ı filtreleme fonksiyonu
  const filterItemsByTab = (items, activeTab, componentType) => {
    if (componentType === "FlightTicketsTab") {
      if (activeTab === 1) {
        const result = items?.CheapestOWFlightsFromOrigin || [];
        return result;
      } else if (activeTab === 2) {
        const result = items?.CheapestRTFlightsFromOrigin || [];
        return result;
      }
    }

    return items || [];
  };

  return (
    <div className="space-y-4 lg:space-y-8">
      <div className="space-y-4">
        {" "}
        <SectionHeader
          title={title}
          description={description}
          linkText={linkText}
          href={href}
          preserveTrailingSlash={preserveTrailingSlash}
          className="pr-4 lg:pr-0"
        />
        {showTabs && effectiveTabs.length > 1 && (
          <TabSwitcher
            tabs={effectiveTabs}
            selected={activeTab}
            onChange={handleTabChange}
          />
        )}
      </div>

      <div className="">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {getCurrentItems().length > 0 ? (
            getCurrentItems()
              .slice(0, 6)
              .map((flight, index) => {
                // Gidiş-dönüş biletlerini kontrol et (IncomingDepartureDateTime varsa)
                const isRoundTrip =
                  flight.IncomingDepartureDateTime ||
                  flight.IncomingDepartureDate;
                return (
                  <FlightTicketCard
                    key={index}
                    flight={flight}
                    isRoundTrip={isRoundTrip}
                    onCardClickOverride={() => handleFlightCardClick(flight)}
                  />
                );
              })
          ) : (
            <div className="col-span-full py-8 text-center">
              <p className="text-gray-500">{t("flight.noFlightData")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
